<template>
  <div
    v-if="thumb"
    class="ScrollProgressBar"
    role="presentation"
  >
    <div
      class="ScrollProgressBar-thumb"
      :style="{ inlineSize: thumb.size, insetInlineStart: thumb.offset }"
    />
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { usePagedScroll } from '@cvo/ui/composables';

export interface ScrollProgressBarProps {
  /** The horizontally scrollable element to track. */
  target?: HTMLElement | null;
}

const props = defineProps<ScrollProgressBarProps>();

const { visibleFraction, scrolledFraction } = usePagedScroll({ target: () => props.target });

/**
 * Thumb size and offset as percentages of the track, mirroring how much of the row is
 * visible and how far it has scrolled. Null while the row fits, so nothing renders.
 */
const thumb = computed(() => {
  if (visibleFraction.value >= 1) return null;

  return {
    size: `${visibleFraction.value * 100}%`,
    offset: `${scrolledFraction.value * 100}%`,
  };
});
</script>

<style lang="scss">
@use '@cvo/ui/styles/design-system' as vars;

@layer cvo-ui {
  .ScrollProgressBar {
    position: relative;
    overflow: hidden;
    block-size: vars.$dimensions-d-1;
    background-color: vars.$atoms-scrollprogressbar-bg-color;
    border-radius: vars.$border-radius-full;

    &-thumb {
      position: absolute;
      inset-block-start: 0;
      block-size: vars.$dimensions-d-1;
      background-color: vars.$atoms-scrollprogressbar-bar-color;
      border-radius: vars.$border-radius-full;
    }
  }
}
</style>
